import {
  ChangeDetectionStrategy,
  Component,
  Signal,
  signal,
  input
} from "@angular/core";
import { ActionSheetController, AlertButton, AlertController, AlertInput, IonicModule, ModalController, NavController, ToastController, isPlatform } from "@ionic/angular";
import { selectSelectedInvoice } from "../../invoice-ngrx/invoices.selectors";
import { Store } from "@ngrx/store";
import { ActivatedRoute } from "@angular/router";
import { BehaviorSubject, Subject, takeUntil } from "rxjs";
import { InvoiceStatusDto } from "../../interfaces/invoice_status_dto";
import { AsyncPipe, DatePipe, NgFor, NgIf, NgStyle } from "@angular/common";
import { TranslateStatusPipe } from "src/app/shared/pipes/translate-status.pipe";
import { DecimalPipe } from "@angular/common";
import { InvoiceDto } from "../../interfaces/invoice_dto";
import { InvoiceDetailPaymentAddRequest } from "../../interfaces/invoice_detail_payment_add_request";
import { DialogService } from "src/app/shared/services/dialog.service";
import { ModeService } from "src/app/utils/mode.service";
import { StudentDto } from "src/app/students/interfaces/student_dto";
import { selectStudentById } from "src/app/students/ngrx/students.selectors";
import { PdfPreviewComponent } from "../../components/pdf-preview/pdf-preview.component";
import { FileService } from "src/app/supabase/services/files.service";
import { AuthService } from "src/app/supabase/services/auth.service";
import { formatDate } from "src/app/utils/DateUtils";
import { Base64Service } from '../../../shared/services/base64.service';
import { MailDialogComponent } from "../../components/mail-dialog/mail-dialog.component";
import { deleteInvoice } from "../../invoice-ngrx/invoices.actions";

@Component({
    selector: "app-invoice-detail-page",
    templateUrl: "./invoice-detail-page.component.html",
    styleUrls: ["./invoice-detail-page.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        IonicModule,
        AsyncPipe,
        DatePipe,
        TranslateStatusPipe,
        NgStyle,
        DecimalPipe,
    ]
})
export class InvoiceDetailPageComponent {
  readonly mobile = input(true);
  isModal: boolean = false;
  originPage: string;
  invoice$ = this.store.select(selectSelectedInvoice);
  studentSignal: Signal<StudentDto> = signal(null);
  history$: BehaviorSubject<InvoiceStatusDto[]> = new BehaviorSubject<
    InvoiceStatusDto[]
  >([]);
  loading$ = new BehaviorSubject<boolean>(false);
  invoice: InvoiceDto;
  formattedDate: string;
  private destroy$ = new Subject<void>();

  ngOnInit() {
    this.invoice$.pipe(takeUntil(this.destroy$)).subscribe(async (invoice) => {
      if (!invoice) return;

      this.studentSignal = this.store.selectSignal(selectStudentById(invoice?.student_id));
      this.invoice = invoice;
      this.history$.next(invoice?.invoice_status);
      // make a copy of invoice.invoice_payment
      let payment_history = invoice?.invoice_payment
        ? [...invoice.invoice_payment]
        : [];
      payment_history = payment_history?.sort((a, b) => {
        return (
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        );
      });

      let total_payments = 0;

      for (let payment of payment_history) {
        total_payments += payment.amount;
        this.history$.next([
          ...this.history$.value,
          {
            status:
              total_payments === invoice.total_amount ? "paid" : "partial",
            created_at: payment.created_at,
          },
        ]);
      }

      // sort by date
      this.history$.next(
        [...this.history$.value].sort((a, b) => {
          return (
            new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
          );
        })
      );
    });

    this.originPage = isPlatform("ios")
      ? this.router.snapshot.paramMap.get("originPage")
      : "";
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ionViewDidLeave() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async createPayment() {
    let today = new Date();
    let formattedDate = today.toISOString().substr(0, 10);

    const payment = {
      date: formattedDate,
      amount: this.invoice.total_amount - this.invoice.paid_amount,
      student: this.studentSignal(),
      invoice_id: this.invoice.invoice_id,
    } as InvoiceDetailPaymentAddRequest;

    await this.dialogService.openNewPaymentDialog(payment);
  }

  async sendReminder() {
    let blobUrl = await this.getPdfSrc();
    var base64 = await this.base64Service.convertToBase64(blobUrl);

    const modal = await this.modalController.create({
      component: MailDialogComponent,
      componentProps: {
        invoiceReminderData: signal({invoice: this.invoice, student: this.studentSignal()}),
        pdf: signal(base64),
      },
    });
    await modal.present();
  }

  closeModal() {
    this.modalController.dismiss();
  }

  async presentInvoiceActionSheet() {
    const actionSheet = await this.actionSheetCtrl.create({
      header: "Aktionen",
      buttons: this.invoiceActionSheetButtons,
    });

    await actionSheet.present();
  }

  public invoiceActionSheetButtons = [
    {
      text: "PDF anzeigen",
      handler: () => {
        this.openPdfPreviewDialog();
      },
    },
    {
      text: "Rechnung löschen",
      role: "destructive",
      handler: () => {
        this.presentDeleteAlert();
      },
    },
    {
      text: "Abbrechen",
      role: "cancel",
      data: {
        action: "cancel",
      },
    },
  ];

  async getPdfSrc() {
    let userId = await this.authService.getCurrentUserId();
    let pdf = await this.fileService.downloadFile("invoices", {
      path: `${userId}/${this.invoice.invoice_id}.pdf`,
      title: "",
    });

    if (pdf === null) {
      this.loading$.next(false);
      const toast = this.toastCtrl.create({
        message: "Rechnung konnte nicht geladen werden",
        duration: 3000,
      });

      toast.then((toast) => toast.present());
      return;
    }
    const blob = new Blob([pdf], { type: "application/pdf" });
    return URL.createObjectURL(blob);
  }

  async openPdfPreviewDialog() {
    this.loading$.next(true);

    let blobUrl = await this.getPdfSrc();
    this.loading$.next(false);

    const modal = this.modalController.create({
      component: PdfPreviewComponent,
      componentProps: {
        pdfSrc: signal(blobUrl),
        fileName: signal(`Rechnung_${this.formattedDate}_${this.studentSignal().surname}.pdf`)
      },
      cssClass: "invoice-preview-modal" + (!isPlatform("mobile") ? ' fullscreen' : ''),
    });
    modal.then((modal) => modal.present());
  }

  async presentDeleteAlert() {
    const alert = await this.alertController.create({
      header: "Achtung!",
      subHeader: "Bist du sicher?",
      message:
        "Diese Aktion kann nicht rückgängig gemacht werden. Zugehörige Zahlungen müssen separat gelöscht werden.",
      buttons: this.deleteAlertButtons,
      inputs: this.deleteAlertInputs,
    });

    alert.present().then(() => {

      /** disable the confirm button initial */
      document.querySelector('ion-alert div.alert-button-group button:nth-of-type(2)').setAttribute('disabled', 'true');
      (document.querySelector('ion-alert div.alert-button-group button:nth-of-type(2)') as HTMLElement).style.opacity = '0.5';
      return;
  });
  }

  public deleteAlertInputs = [
    {
      type: "checkbox" as "checkbox",
      label: "Ich bestätige, dass ich die Rechnung unwiderruflich löschen möchte.",
      handler: () => {
        this.handleConfirmButtonState();
      }
    } as AlertInput
  ]

  handleConfirmButtonState() {
    if (document.querySelector('ion-alert div.alert-button-group button:nth-of-type(2)').hasAttribute('disabled')) {
      document.querySelector('ion-alert div.alert-button-group button:nth-of-type(2)').removeAttribute('disabled');
      (document.querySelector('ion-alert div.alert-button-group button:nth-of-type(2)') as HTMLElement).style.opacity = '1';
    }
    else {
      document.querySelector('ion-alert div.alert-button-group button:nth-of-type(2)').setAttribute('disabled', 'true');
      (document.querySelector('ion-alert div.alert-button-group button:nth-of-type(2)') as HTMLElement).style.opacity = '0.5';
    }
  }

  public deleteAlertButtons = [
    {
      text: "Abbrechen",
      role: "cancel",
      handler: () => {
        this.alertController.dismiss();
      },
    },
    {
      text: "OK",
      role: "confirm",
      disabled: true,
      handler: () => {
        this.deleteInvoice();
        this.alertController.dismiss();
      },
      cssClass: "my-button-disabled"
    } as AlertButton,
  ];

  async deleteInvoice() {
    this.store.dispatch(deleteInvoice({invoiceId: this.invoice.invoice_id}));
    this.navController.pop();
    this.modalController.dismiss();
  }

  constructor(
    private store: Store,
    private navController: NavController,
    public modeService: ModeService,
    private router: ActivatedRoute,
    public modalController: ModalController,
    private dialogService: DialogService,
    private actionSheetCtrl: ActionSheetController,
    private fileService: FileService,
    private authService: AuthService,
    private toastCtrl: ToastController,
    private base64Service: Base64Service,
    private alertController: AlertController
  ) {
    const today = new Date();
    this.formattedDate = formatDate(today);
  }
}
